import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Spinner, Text, Flex, SimpleGrid } from '@chakra-ui/react';
import { TableOptions, DataTable, type Column } from '@themes/aegix';
import { type ResourceCreate } from '../../services';
import { HiChevronDown, HiChevronRight, HiMapPin } from 'react-icons/hi2';
import { DrawerComponent } from '../ui/drawerComponent';
import { Button as ButtonComponent } from '@themes/aegix';
import { useForm, FormProvider, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ResourceForm from '../forms/ResourceForm';
import { resourceFormSchema } from '../../validations/resource.validation';
import {
  useResourcesListData,
  type ResourceRow,
} from './hooks/useResourcesListData';

// columns built inside component

export function ResourcesList() {
  const { t } = useTranslation();

  const columns = useMemo<import('@themes/aegix').Column<ResourceRow>[]>(() => [
    { key: 'name', label: t('resources.col.resource', 'Resource'), sortable: true, width: '200px' },
    { key: 'type', label: t('resources.col.type', 'Type'), sortable: true, width: '150px' },
    {
      key: 'latitude',
      label: t('resources.col.location', 'Location'),
      sortable: false,
      width: '180px',
      render: (row) =>
        row.latitude !== '-' ? (
          <Flex alignItems="center" gap={1}>
            <HiMapPin size={14} />
            <Text fontSize="xs" color="text.secondary">
              {row.latitude}, {row.longitude}
            </Text>
          </Flex>
        ) : (
          <Text fontSize="xs" color="text.secondary">—</Text>
        ),
    },
  ], [t]);

  const {
    searchValue,
    setSearchValue,
    expandedSites,
    editOpen,
    action,
    currentRow,
    isLoading,
    siteGroups,
    poiTypesOptions,
    siteOptions,
    createMutationPending,
    updateMutationPending,
    handleDrawerClose,
    toggleSite,
    handleAddResource,
    handleSubmit,
    handleEditResource,
    handleDeleteResource,
  } = useResourcesListData();

  const methods = useForm<ResourceCreate>({
    defaultValues: {
      name: '',
      type: '',
    },
    resolver: zodResolver(
      resourceFormSchema,
    ) as unknown as Resolver<ResourceCreate>,
  });
  // enableReinitialize equivalent
  useEffect(() => {
    methods.reset({
      name: currentRow?.name || '',
      type: currentRow?.type || '',
      school: currentRow?.school,
      room: currentRow?.room,
    });
  }, [currentRow, methods]);

  return (
    <>
      <TableOptions
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        showFilter={false}
        primaryAction={{
          label: t('resources.addResource', 'Add Resource'),
          onClick: handleAddResource,
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : siteGroups.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('resources.noResourcesFound', 'No resources found')}</Text>
          </Center>
        ) : (
          <Box>
            {siteGroups.map((group) => {
              const isExpanded = expandedSites.has(group.siteId);

              return (
                <Box
                  key={group.siteId}
                  mb={3}
                  border="1px solid"
                  borderColor="border.secondary"
                  borderRadius="lg"
                  overflow="hidden"
                  data-e2e={`site-group-${group.siteId}`}
                >
                  <Flex
                    px={4}
                    py={3}
                    alignItems="center"
                    justifyContent="space-between"
                    cursor="pointer"
                    bg="surface.secondary"
                    _hover={{ bg: 'surface.tertiary' }}
                    onClick={() => toggleSite(group.siteId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSite(group.siteId);
                      }
                    }}
                  >
                    <Flex alignItems="center" gap={2}>
                      {isExpanded ? (
                        <HiChevronDown size={18} />
                      ) : (
                        <HiChevronRight size={18} />
                      )}
                      <Text fontWeight={600} fontSize="sm" color="text.primary">
                        {group.siteName}
                      </Text>
                    </Flex>
                    <Text
                      fontSize="xs"
                      color="text.secondary"
                      data-e2e="resource-count"
                    >
                      {t('resources.resourceCount', '{{count}} resources', { count: group.resources.length })}
                    </Text>
                  </Flex>

                  {isExpanded && (
                    <Box>
                      {group.resources.length === 0 ? (
                        <Center py={6}>
                          <Text fontSize="sm" color="text.secondary">
                            {t('resources.noResourcesForSite', 'No resources for this site')}
                          </Text>
                        </Center>
                      ) : (
                        <DataTable
                          columns={columns}
                          data={group.resources}
                          keyExtractor={(row) => row.id}
                          onRowClick={(row) => handleEditResource(row)}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        <DrawerComponent
          isOpen={editOpen}
          onClose={() => handleDrawerClose()}
          title={action === 'add' ? t('resources.addNewResource', 'Add New Resource') : t('resources.editResource', 'Edit Resource')}
          e2eTitle="resource-edit-drawer"
          size="sm"
          closeOnOutside={false}
          closeOnEsc={false}
          footer={
            <SimpleGrid columns={currentRow?.id ? 3 : 2} gap={4} width={'100%'}>
              {action === 'edit' && (
                <ButtonComponent
                  visual="alert"
                  onClick={() => handleDeleteResource()}
                  data-e2e="delete-resource-button"
                >
                  Delete
                </ButtonComponent>
              )}
              <ButtonComponent
                visual="secondary"
                onClick={() => handleDrawerClose()}
                data-e2e="cancel-resource-button"
              >{t('common.cancel', 'Cancel')}
              </ButtonComponent>
              <ButtonComponent
                visual="primary"
                type="submit"
                form="resource-form"
                id="resource-form"
                disabled={createMutationPending || updateMutationPending}
                data-e2e="save-resource-button"
              >
                {action === 'add' ? t('common.save', 'Save') : t('common.update', 'Update')}
              </ButtonComponent>
            </SimpleGrid>
          }
        >
          <FormProvider {...methods}>
            <form
              id="resource-form"
              onSubmit={methods.handleSubmit((values) => {
                handleSubmit(values);
              })}
            >
              <ResourceForm
                siteOptions={siteOptions}
                poiTypes={poiTypesOptions}
                action={action}
              />
            </form>
          </FormProvider>
        </DrawerComponent>
      </Box>
    </>
  );
}
